import React from "react";
import { Icon } from "@/components/Icon";
import { Tooltip } from "@/components/Tooltip";

export type BadgeVariant =
    | "primary"
    | "primaryinverted"
    | "white"
    | "secondary"
    | "success"
    | "danger"
    | "error"
    | "warning"
    | "info"
    | "neutral"
    | "transparent"
    | "purple"
    | "cyan"
    | "orange";

type BadgeSize = "xs" | "sm" | "md" | "lg";

interface BadgeProps {
    children?: React.ReactNode;
    variant?: BadgeVariant;
    /** Either a HEX color (e.g. '#10b981') or a tag / class name (e.g. 'success', 'green', 'warning', 'blue') */
    color?: string | null;
    style?: React.CSSProperties;
    size?: BadgeSize;
    className?: string;
    /** Show or hide the border. Default: true */
    border?: boolean;
    /** Rounded-full (true, default) or rounded-md (false) */
    pill?: boolean;
    /** Show a dot indicator before the text */
    dot?: boolean;
    /** Optional lucide icon name (from Icon.tsx) */
    icon?: string;
    /** Optional click handler */
    onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
    /** Optional link value */
    link?: string;
    /** Optional tooltip title */
    title?: string;
    /** Compact circular badge for counts (e.g. attachment totals) */
    count?: boolean;
    /** Optional count value */
    value?: number | string;
}

const VARIANT_BASE: Record<BadgeVariant, string> = {
    primary: "bg-primary text-white",
    primaryinverted: "bg-white text-primary border-primary",
    white: "bg-white text-primary",
    secondary:
        "bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-zinc-300",
    success:
        "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300",
    danger: "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300",
    error: "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300",
    warning:
        "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300",
    info: "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300",
    neutral: "bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-zinc-300",
    transparent: "text-slate-600 dark:text-zinc-300",
    purple: "bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300",
    cyan: "bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300",
    orange: "bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300",
};

const VARIANT_BORDER: Record<BadgeVariant, string> = {
    primary: "border border-primary dark:border-primary-400",
    primaryinverted: "border border-primary dark:border-primary-400",
    white: "",
    secondary: "border border-slate-200 dark:border-white/10",
    success: "border border-emerald-200 dark:border-emerald-800/60",
    danger: "border border-rose-200 dark:border-rose-800/60",
    error: "border border-rose-200 dark:border-rose-800/60",
    warning: "border border-amber-200 dark:border-amber-800/60",
    info: "border border-blue-200 dark:border-blue-800/60",
    neutral: "border border-slate-200 dark:border-white/10",
    transparent: "border border-slate-200 dark:border-white/10",
    purple: "border border-purple-200 dark:border-purple-800/60",
    cyan: "border border-cyan-200 dark:border-cyan-800/60",
    orange: "border border-orange-200 dark:border-orange-800/60",
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
    xs: "h-4 px-[4px] text-[9px] leading-none",
    sm: "h-5 px-[6px] text-[10px] leading-none",
    md: "h-6 px-[7px] text-xs leading-none",
    lg: "h-7 px-[8px] text-sm leading-none",
};

const DOT_CLASSES: Record<BadgeVariant, string> = {
    primary: "bg-white",
    primaryinverted: "bg-primary",
    white: "bg-white",
    secondary: "bg-slate-500",
    success: "bg-emerald-500",
    danger: "bg-rose-500",
    error: "bg-rose-500",
    warning: "bg-amber-500",
    info: "bg-blue-500",
    neutral: "bg-slate-400",
    transparent: "bg-slate-400",
    purple: "bg-purple-500",
    cyan: "bg-cyan-500",
    orange: "bg-orange-500",
};

const ICON_SIZE: Record<BadgeSize, number> = {
    xs: 8,
    sm: 10,
    md: 12,
    lg: 14,
};

const COUNT_SIZE_CLASSES: Record<BadgeSize, string> = {
    xs: "min-w-4 h-4 px-1 text-[8px] leading-none",
    sm: "min-w-5 h-5 px-1 text-[10px] leading-none",
    md: "min-w-6 h-6 px-1.5 text-xs leading-none",
    lg: "min-w-7 h-7 px-2 text-sm leading-none",
};

const isHexColor = (val: string): boolean => {
    return /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(
        val.trim()
    );
};

const normalizeHex = (val: string): string => {
    const trimmed = val.trim();
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
};

const resolveTagToVariant = (tag: string): BadgeVariant | null => {
    const t = tag.trim().toLowerCase();
    switch (t) {
        case "green":
        case "emerald":
        case "lime":
        case "success":
            return "success";
        case "yellow":
        case "amber":
        case "warning":
            return "warning";
        case "blue":
        case "sky":
        case "info":
            return "info";
        case "red":
        case "rose":
        case "danger":
        case "error":
            return "danger";
        case "primary":
            return "primary";
        case "primaryinverted":
            return "primaryinverted";
        case "neutral":
        case "gray":
        case "grey":
        case "slate":
        case "zinc":
            return "neutral";
        case "purple":
        case "violet":
        case "indigo":
            return "purple";
        case "cyan":
        case "teal":
            return "cyan";
        case "orange":
            return "orange";
        case "white":
            return "white";
        case "transparent":
            return "transparent";
        case "secondary":
            return "secondary";
        default:
            return null;
    }
};

export const Badge: React.FC<BadgeProps> = ({
    children,
    variant = "secondary",
    color = null,
    style,
    size = "md",
    className = "",
    border = true,
    pill = true,
    dot = false,
    icon,
    onClick,
    link = null,
    title,
    count = false,
    value = null,
}) => {
    let effectiveVariant = variant;
    let customStyle: React.CSSProperties | undefined = style;
    let isCustomHex = false;
    let customClassFromColor = "";

    if (color) {
        if (isHexColor(color)) {
            isCustomHex = true;
            const hex = normalizeHex(color);
            customStyle = {
                backgroundColor: `${hex}1a`,
                borderColor: `${hex}4d`,
                color: hex,
                ...style,
            };
        } else {
            const resolved = resolveTagToVariant(color);
            if (resolved) {
                effectiveVariant = resolved;
            } else {
                customClassFromColor = color.trim();
            }
        }
    }

    const shape = pill || count ? "rounded-full" : "rounded-md";
    const borderClass =
        border && !count
            ? isCustomHex
                ? "border"
                : customClassFromColor
                  ? "border"
                  : VARIANT_BORDER[effectiveVariant]
            : "";
    const sizeClass = count ? COUNT_SIZE_CLASSES[size] : SIZE_CLASSES[size];
    const hasAction = onClick || link;
    const clickClass = hasAction
        ? "cursor-pointer hover:opacity-80 transition-opacity active:scale-95"
        : "";
    const action = onClick
        ? onClick
        : link
          ? () => (window.location.href = link)
          : undefined;

    const baseClass = isCustomHex
        ? ""
        : customClassFromColor || VARIANT_BASE[effectiveVariant];

    const badgeElement = (
        <span
            onClick={action}
            role={hasAction ? "button" : undefined}
            tabIndex={hasAction ? 0 : undefined}
            style={customStyle}
            className={`inline-flex items-center justify-center gap-1.5 font-semibold select-none whitespace-nowrap ${shape} ${baseClass} ${borderClass} ${sizeClass} ${clickClass} ${className}`}
        >
            {dot && (
                <span
                    style={
                        isCustomHex && color
                            ? { backgroundColor: normalizeHex(color) }
                            : undefined
                    }
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${isCustomHex ? "" : DOT_CLASSES[effectiveVariant]}`}
                />
            )}
            {icon && (
                <Icon
                    value={icon}
                    size={ICON_SIZE[size]}
                    className="shrink-0"
                />
            )}
            {value ? value : children}
        </span>
    );

    if (title) {
        return (
            <Tooltip title={title} placement="top">
                {badgeElement}
            </Tooltip>
        );
    }

    return badgeElement;
};

export default Badge;
